import { AlertRulesCheckResult, AlertRulesFileResult } from '@apm/contracts';
import { useCallback, useEffect, useRef, useState } from 'react';
import { failureMessage, getJson, readJson, request } from '../api';

/**
 * 규칙 파일 편집 (24회차, 제안 9).
 *
 * `useQuery`는 읽기만 하는 훅이라 여기서는 쓰지 않는다. 이 화면은 파일 전문을 한 번 읽어
 * 편집 상자에 넣고, 검증·적용 두 자리에서 서버로 되보낸다.
 *
 * 서버가 준 원본(`saved`)을 따로 들고 있는 이유는 하나다 — 고친 것이 있는지 알아야
 * "되돌리기"를 켜고 끌 수 있고, 적용한 뒤에는 그 값이 새 원본이 된다.
 */

const FILE_URL = '/api/alerts/rules/file';

export interface RuleFileState {
  loading: boolean;
  /** 검증·적용이 도는 중. 단추를 두 번 누르지 못하게 한다 */
  busy: boolean;
  /** 편집 상자의 지금 값 */
  text: string;
  /** 서버에 있는 값. 이것과 다르면 고친 것이 있다 */
  saved: string;
  /** 이 편집기가 연 판. 저장할 때 그대로 돌려보내 그 사이 바뀌었는지 본다 (24회차 리뷰 5) */
  digest: string;
  file: string;
  readable: boolean;
  error?: string;
  /** 마지막 검증·적용 결과 */
  result?: AlertRulesCheckResult;
  setText: (value: string) => void;
  /** 편집한 것을 버리고 서버 값으로 되돌린다 */
  revert: () => void;
  reload: () => void;
  check: () => Promise<void>;
  apply: () => Promise<void>;
}

export function useRuleFile(onApplied?: () => void): RuleFileState {
  const [state, setState] = useState<{
    loading: boolean;
    busy: boolean;
    text: string;
    saved: string;
    digest: string;
    file: string;
    readable: boolean;
    error?: string;
    result?: AlertRulesCheckResult;
  }>({
    loading: true,
    busy: false,
    text: '',
    saved: '',
    digest: '',
    file: '',
    readable: false,
  });
  const [tick, setTick] = useState(0);
  /** 보낼 값을 콜백이 그때그때 읽는다. 상태를 갱신 함수 안에서 훔쳐보지 않기 위해서다 */
  const latest = useRef({ text: '', digest: '' });
  latest.current = { text: state.text, digest: state.digest };

  useEffect(() => {
    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true }));
    getJson(FILE_URL, AlertRulesFileResult, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({
          loading: false,
          busy: false,
          text: data.text,
          saved: data.text,
          digest: data.digest,
          file: data.file,
          readable: data.readable,
          error: undefined,
          result: undefined,
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        }));
      });
    return () => controller.abort();
  }, [tick]);

  const send = useCallback(
    async (mode: 'check' | 'apply') => {
      const { text, digest } = latest.current;
      setState((prev) => ({ ...prev, busy: true, error: undefined }));
      try {
        // 닿지 못함·계약 불일치의 문구는 `api.ts`가 한곳에서 정한다 (44회차)
        const response = await request(
          mode === 'check' ? '/api/alerts/rules/check' : '/api/alerts/rules',
          {
            method: mode === 'check' ? 'POST' : 'PUT',
            headers: { 'content-type': 'application/json' },
            // 저장에는 연 판을 함께 보낸다. 그 사이 누가 고쳤으면 서버가 409로 막는다
            body: JSON.stringify(mode === 'check' ? { text } : { text, digest }),
          },
        );
        if (!response.ok) {
          throw new Error(
            await failureMessage(
              response,
              mode === 'check' ? '검증하지 못했다' : '규칙을 적용하지 못했다',
            ),
          );
        }
        const result = await readJson(response, AlertRulesCheckResult);
        setState((prev) => ({
          ...prev,
          busy: false,
          result,
          // 적용에 성공했을 때만 서버 값이 바뀐다
          saved: result.saved ? text : prev.saved,
          digest: result.saved && result.digest !== undefined ? result.digest : prev.digest,
        }));
        // 규칙이 바뀌었으니 위 규칙 표·통계·이력도 같은 시점에 다시 읽는다 (24회차 리뷰 10)
        if (result.saved) onApplied?.();
      } catch (err) {
        setState((prev) => ({
          ...prev,
          busy: false,
          error: err instanceof Error ? err.message : String(err),
        }));
      }
    },
    [onApplied],
  );

  return {
    ...state,
    setText: useCallback((value: string) => {
      setState((prev) => ({ ...prev, text: value, result: undefined }));
    }, []),
    revert: useCallback(() => {
      setState((prev) => ({ ...prev, text: prev.saved, result: undefined, error: undefined }));
    }, []),
    reload: useCallback(() => setTick((value) => value + 1), []),
    check: useCallback(() => send('check'), [send]),
    apply: useCallback(() => send('apply'), [send]),
  };
}
